/**
 * Dokumenter on the employee card: what is filed for this person, and what the
 * company has published. ADR 0020, plan 0020 stage 4.
 *
 * Behind `documents.manage`. Filing and publishing are the administrator's;
 * employees read their own copy on Min side and cannot write (decision 5).
 *
 * This replaces the panel that drove /files/:userId directly. That one wrote an
 * object to the bucket and no database row, so what an administrator filed was
 * invisible to the employee for good and counted toward no quota.
 */
import { errorText } from '@/api/client';
import {
  type ApiDocument,
  type OnConflict,
  downloadDocument,
  fileDocument,
  removeDocument,
  useAdminDocuments,
  useDocumentUsage,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { DokumentLesere } from '@/pages/DokumentLesere';
import { Button, Empty, Panel, Skeleton, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

const kb = (n: number) =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;

/** The file waiting on an answer about its name. */
interface Pending {
  file: File;
  notify: boolean;
}

export function Files({ userId }: { userId: number }) {
  const can = useCan();
  const allowed = can('ansatte.dokumenter');
  const [shared, setShared] = useState(false);
  const owner = shared ? null : userId;
  const q = useAdminDocuments(owner, allowed);
  const usage = useDocumentUsage(allowed);
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [notify, setNotify] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [showReaders, setShowReaders] = useState<number | null>(null);

  if (!allowed) return null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-documents', owner] });
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['document-usage'] });
  };

  const send = async (file: File, notifyThem: boolean, onConflict?: OnConflict) => {
    setBusy(true);
    try {
      await fileDocument({ file, ownerUserId: owner, notify: notifyThem, onConflict });
      setPending(null);
      refresh();
      toast(T('Lagret'));
    } catch (e) {
      // The server refuses a name it already has rather than guessing which of
      // the three this is, so ask.
      const status = (e as { status?: number }).status;
      if (status === 409) setPending({ file, notify: notifyThem });
      // 507: the company is full, not the file too big. Nothing the administrator
      // can fix from here, so say who can.
      else if (status === 507) toast(T('Dokumentlagringen er full. Kontakt support for mer plass.'));
      else toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const open = async (doc: ApiDocument) => {
    try {
      await downloadDocument(doc);
    } catch (e) {
      toast(errorText(e));
    }
  };

  const remove = async (doc: ApiDocument) => {
    const reason = window.prompt(T('Hvorfor fjernes dokumentet?'));
    if (!reason?.trim()) return;
    try {
      await removeDocument(doc.id, reason.trim());
      refresh();
      toast(T('Fjernet'));
    } catch (e) {
      toast(errorText(e));
    }
  };

  const docs = q.data ?? [];

  return (
    <Panel
      title={T('Dokumenter')}
      sub={
        <span className="seg" style={{ padding: 2 }}>
          <button
            type="button"
            aria-pressed={!shared}
            onClick={() => setShared(false)}
            style={{ padding: '2px 8px', fontSize: 12 }}
          >
            {T('Personlig')}
          </button>
          <button
            type="button"
            aria-pressed={shared}
            onClick={() => setShared(true)}
            style={{ padding: '2px 8px', fontSize: 12 }}
          >
            {T('Felles')}
          </button>
        </span>
      }
      actions={
        <>
          <label style={{ fontSize: 12, marginRight: 8 }}>
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              aria-label={T('Varsle')}
            />{' '}
            {T('Varsle')}
          </label>
          <input
            ref={input}
            type="file"
            hidden
            aria-label={T('Velg fil')}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) send(f, notify);
              e.target.value = '';
            }}
          />
          <Button small disabled={busy} onClick={() => input.current?.click()}>
            {shared ? T('Publiser') : T('Legg inn')}
          </Button>
        </>
      }
      flush
    >
      {usage.data && usage.data.percent >= 80 ? (
        // The warning has to be where an administrator is when it matters, which
        // is here, filing. Refusal at the limit was the owner's choice (ADR 0020,
        // decision 18), so the run-up to it must not be buried in settings.
        <div className="al warn" style={{ margin: 12 }} data-testid="storage-warning">
          <span>
            <Icon name="warn" />
          </span>
          <span>
            {T('Dokumentlagringen er')} {usage.data.percent}% {T('full')} ({kb(usage.data.usedBytes)}{' '}
            {T('av')} {kb(usage.data.allowanceBytes)}).{' '}
            {usage.data.percent >= 100
              ? T('Nye dokumenter blir avvist. Kontakt support for mer plass.')
              : T('Kontakt support før den er full.')}
          </span>
        </div>
      ) : null}

      {pending ? (
        <div className="al warn" style={{ margin: 12 }} data-testid="name-clash">
          <span>
            <Icon name="warn" />
          </span>
          <span>
            {T('Et dokument med dette navnet finnes allerede')}: {pending.file.name}.
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button small disabled={busy} onClick={() => send(pending.file, pending.notify, 'new-version')}>
                {T('Ny versjon')}
              </Button>
              <Button small disabled={busy} onClick={() => send(pending.file, false, 'correction')}>
                {T('Rettelse')}
              </Button>
              <Button small disabled={busy} onClick={() => send(pending.file, pending.notify, 'keep-both')}>
                {T('Behold begge')}
              </Button>
              <Button small disabled={busy} onClick={() => setPending(null)}>
                {T('Avbryt')}
              </Button>
            </div>
            <div className="hint" style={{ marginTop: 6 }}>
              {T(
                'Ny versjon erstatter den gamle og alle må lese den på nytt. Rettelse bytter innholdet uten å varsle. Behold begge lagrer den ved siden av.',
              )}
            </div>
          </span>
        </div>
      ) : null}

      {q.isError ? (
        <div className="al warn" style={{ margin: 12 }} data-testid="files-error">
          <span>
            <Icon name="warn" />
          </span>
          <span>
            {T('Dokumentene er ikke tilgjengelige')}: {(q.error as Error).message}.
          </span>
        </div>
      ) : q.isPending ? (
        <Skeleton />
      ) : docs.length ? (
        docs.map((d) => (
          <div className="frow" key={d.id}>
            <span>
              <Icon name="document" />
            </span>
            <span>
              {d.name}
              <br />
              <small>
                {shared ? T('felles, alle ansatte') : T('personlig')}, {kb(d.size)}
                {d.version > 1 ? `, ${T('versjon')} ${d.version}` : ''}, {fmtD(d.createdAt.slice(0, 10))}
              </small>
            </span>
            <Button small onClick={() => open(d)} aria-label={`${T('Åpne')} ${d.name}`}>
              {T('Åpne')}
            </Button>
            <Button
              small
              onClick={() => setShowReaders(showReaders === d.id ? null : d.id)}
              aria-label={`${T('Hvem har lest')} ${d.name}`}
            >
              {T('Lest av')}
            </Button>
            <Button small onClick={() => remove(d)} aria-label={`${T('Fjern')} ${d.name}`}>
              {T('Fjern')}
            </Button>
            {showReaders === d.id ? <DokumentLesere id={d.id} /> : null}
          </div>
        ))
      ) : (
        <Empty>{shared ? T('Ingen felles dokumenter.') : T('Ingen personlige dokumenter.')}</Empty>
      )}
      <p className="hint" style={{ padding: '8px 14px' }}>
        {T(
          'Personlige dokumenter gjelder én person, felles dokumenter deles av alle i bedriften. Den ansatte ser begge under Min side, og ser hvem som har åpnet sine egne.',
        )}
      </p>
    </Panel>
  );
}
